import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, FormControl, InputLabel, Select, MenuItem,
  Alert, Box,
} from '@mui/material';
import { apiService } from '../services/apiService';

const CreateProjectMotdDialog = ({ open, onClose, projectId, projectName, onCreated }) => {
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState('info');
  const [deliveryType, setDeliveryType] = useState('message');
  const [expiresAt, setExpiresAt] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const getDefaultExpiry = () => {
    const d = new Date();
    d.setDate(d.getDate() + 7);
    return d.toISOString().slice(0, 16);
  };

  const handleSubmit = async () => {
    if (!title.trim() || !message.trim()) {
      setError('Title and message are required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await apiService.createSystemMotd({
        title: title.trim(),
        message: message.trim(),
        severity,
        delivery_type: deliveryType,
        expires_at: new Date(expiresAt || getDefaultExpiry()).toISOString(),
        target: {
          target_type: 'selected',
          project_ids: [projectId],
        },
      });

      if (result.success) {
        setTitle('');
        setMessage('');
        setSeverity('info');
        setDeliveryType('message');
        setExpiresAt('');
        onCreated?.(result.data);
        onClose();
      } else {
        setError(result.error || 'Failed to create announcement');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create announcement');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Send Announcement — {projectName}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <Alert severity="info" sx={{ py: 0.5 }}>
            This announcement will be sent to all members of {projectName}.
          </Alert>

          <TextField label="Title" value={title} onChange={(e) => setTitle(e.target.value)} fullWidth required inputProps={{ maxLength: 200 }} />
          <TextField label="Message" value={message} onChange={(e) => setMessage(e.target.value)} fullWidth required multiline rows={4} inputProps={{ maxLength: 5000 }} />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Severity</InputLabel>
              <Select value={severity} onChange={(e) => setSeverity(e.target.value)} label="Severity">
                <MenuItem value="info">Info</MenuItem>
                <MenuItem value="warning">Warning</MenuItem>
                <MenuItem value="critical">Critical</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Delivery</InputLabel>
              <Select value={deliveryType} onChange={(e) => setDeliveryType(e.target.value)} label="Delivery">
                <MenuItem value="message">Message only</MenuItem>
                <MenuItem value="email">Email only</MenuItem>
                <MenuItem value="both">Message + Email</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <TextField label="Expires At" type="datetime-local" value={expiresAt || getDefaultExpiry()} onChange={(e) => setExpiresAt(e.target.value)} fullWidth InputLabelProps={{ shrink: true }} />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading || !title.trim() || !message.trim()}>
          {loading ? 'Sending...' : deliveryType === 'message' ? 'Create' : 'Create & Send'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateProjectMotdDialog;
